import React, { useMemo } from 'react';
import { captureDetailHasQuantity } from '../../utils/fermer-enregistrement-capture.js';

const PECHE_QUANTITE_FIELDS = [
    'completQuantite',
    'eviscereQuantite',
    'eteteQuantite',
    'consommeQuantite',
    'nonPeseeQuantite',
    'remisAEau',
];

const chasseDetailHasQuantity = (detail) =>
    Number(detail.nombreCaptureRapportee) > 0
    || Number(detail.nombreCaptureConsommee) > 0;

const sumQuantities = (detail, fields) =>
    fields.reduce((total, field) => total + (Number(detail[field]) || 0), 0);

const getPecheTotal = (detail) => sumQuantities(detail, PECHE_QUANTITE_FIELDS);

const getChasseTotal = (detail) =>
    sumQuantities(detail, ['nombreCaptureRapportee', 'nombreCaptureConsommee']);

const formatPecheDetailSommaire = (detail, texts) => {
    const parts = [];
    const addQuantity = (qty, label, masse = null) => {
        if (Number(qty) > 0) {
            let entry = `${qty} ${label}`;
            if (masse !== null && Number(masse) > 0) {
                entry += ` (${masse} kg)`;
            }
            parts.push(entry);
        }
    };

    addQuantity(detail.completQuantite, texts.groupComplet, detail.completMasse);
    addQuantity(detail.eviscereQuantite, texts.groupEviscere, detail.eviscereMasse);
    addQuantity(detail.eteteQuantite, texts.groupEtete, detail.eteteMasse);
    addQuantity(detail.consommeQuantite, texts.groupConsomme);
    addQuantity(detail.nonPeseeQuantite, texts.groupNonPese);
    addQuantity(detail.remisAEau, texts.groupRemisEau);

    return parts.join(', ');
};

const formatChasseDetailSommaire = (detail, texts) => {
    const parts = [];
    if (Number(detail.nombreCaptureRapportee) > 0) {
        parts.push(`${detail.nombreCaptureRapportee} ${texts.labelRapportees}`);
    }
    if (Number(detail.nombreCaptureConsommee) > 0) {
        parts.push(`${detail.nombreCaptureConsommee} ${texts.labelConsommees}`);
    }
    return parts.join(', ');
};

const buildVerificationSections = ({ mode, blocks, allLocations, texts }) => {
    const isChasse = mode === 'chasse';
    const locationIdKey = isChasse ? 'idSecteur' : 'idPointEau';
    const locationLabelKey = isChasse ? 'nomSecteur' : 'description';
    const hasQuantity = isChasse ? chasseDetailHasQuantity : captureDetailHasQuantity;
    const formatSommaire = isChasse ? formatChasseDetailSommaire : formatPecheDetailSommaire;
    const getTotal = isChasse ? getChasseTotal : getPecheTotal;

    const sections = blocks
        .filter(block => block[locationIdKey])
        .map(block => {
            const location = allLocations.find(item => item[locationIdKey] === block[locationIdKey]);
            const locationLabel = location?.[locationLabelKey]
                || location?.NomSecteur
                || block[locationIdKey];

            const entries = (block.capturesDetails || [])
                .filter(hasQuantity)
                .map(detail => ({
                    id: isChasse ? detail.idGibier : detail.idEspece,
                    total: getTotal(detail),
                    speciesLabel: detail.descriptionEspece,
                    sommaire: formatSommaire(detail, texts),
                }));

            return {
                id: block.id || block[locationIdKey],
                locationLabel,
                aEuDesPrises: block.aEuDesPrises,
                entries,
            };
        });

    const grandTotal = sections.reduce(
        (sum, section) => sum + section.entries.reduce((entrySum, entry) => entrySum + entry.total, 0),
        0
    );

    return { sections, grandTotal };
};

const CaptureVerificationSommaire = ({ texts, mode, blocks, allLocations }) => {
    const isChasse = mode === 'chasse';
    const { sections, grandTotal } = useMemo(
        () => buildVerificationSections({ mode, blocks, allLocations, texts }),
        [mode, blocks, allLocations, texts]
    );

    if (!sections.length) {
        return null;
    }

    return (
        <div className="fec-capture-verification">
            <div className="fec-capture-verification-title">
                <span className="droit-acces-span">{texts.captureVerificationTitle}</span>
            </div>
            {sections.map(section => (
                <div key={section.id} className="fec-capture-verification-location">
                    <div className="fec-capture-verification-location-name">
                        <span className="droit-acces-span">{section.locationLabel}</span>
                    </div>
                    {section.aEuDesPrises === false &&
                        <div className="fec-capture-verification-empty">
                            <span className="droit-acces-span">{texts.captureVerificationNoCatch}</span>
                        </div>
                    }
                    {section.aEuDesPrises === true && section.entries.length === 0 &&
                        <div className="fec-capture-verification-empty">
                            <span className="droit-acces-span">
                                {isChasse ? texts.captureVerificationNoGibier : texts.captureVerificationNoSpecies}
                            </span>
                        </div>
                    }
                    {section.entries.map(entry => (
                        <div key={entry.id} className="fec-capture-verification-entry">
                            <span className="fec-capture-verification-species droit-acces-span">
                                <span className="fec-capture-verification-total">{entry.total}</span>
                                <span className="fec-capture-verification-species-name">{entry.speciesLabel}</span>
                            </span>
                            <span className="fec-capture-verification-detail droit-acces-span">
                                {entry.sommaire}
                            </span>
                        </div>
                    ))}
                </div>
            ))}
            {grandTotal > 0 &&
                <div className="fec-capture-verification-entry fec-capture-verification-grand-total">
                    <span className="fec-capture-verification-species droit-acces-span">
                        <span className="fec-capture-verification-total">{grandTotal}</span>
                        {texts.total}
                    </span>
                </div>
            }
        </div>
    );
};

export default CaptureVerificationSommaire;
